import { parseKickoffUtcMs } from "@/lib/kickoffTime";

/**
 * Faslås (samma princip som backend/match_bet_lock.py):
 * — Kickoff tolkas som UTC (se kickoffTime.js).
 * — Gruppspels matcher (GROUP/LP): låsta när den tidigaste gruppspelsmatchen med båda lag startat.
 * — Tabelltips och extra (finalister, vinnare, skyttekung): samma lås som gruppspelet.
 * — Slutspel: per match — tippa när båda lagkoder finns, lås när just den matchen startat.
 * — Slutspel utan båda lagkoder (TBD): fortfarande ospelbar tills lag finns.
 */

/** @param {Array<{ stage?: string, home_code?: string, away_code?: string, kickoff?: string, status?: string }>|undefined|null} fixtures */
export function isGroupPhaseLocked(fixtures) {
  const rows = (fixtures || []).filter(
    (fx) => (fx.stage === "GROUP" || fx.stage === "LP") && fx.home_code && fx.away_code,
  );
  if (!rows.length) return false;
  rows.sort(
    (a, b) => (parseKickoffUtcMs(a.kickoff) ?? Infinity) - (parseKickoffUtcMs(b.kickoff) ?? Infinity),
  );
  const first = rows[0];
  if (first.status === "LIVE" || first.status === "FINISHED") return true;
  const kt = parseKickoffUtcMs(first.kickoff);
  return kt != null && kt <= Date.now();
}

/** @param {Array<{ stage?: string, kickoff?: string, status?: string }>|undefined|null} fixtures */
export function isKnockoutPhaseLocked(fixtures) {
  const rows = (fixtures || []).filter((fx) => {
    const st = fx.stage || "";
    if (st === "GROUP" || st === "LP") return false;
    return !!fx.kickoff;
  });
  if (!rows.length) return false;
  rows.sort(
    (a, b) => (parseKickoffUtcMs(a.kickoff) ?? Infinity) - (parseKickoffUtcMs(b.kickoff) ?? Infinity),
  );
  const first = rows[0];
  if (first.status === "LIVE" || first.status === "FINISHED") return true;
  const kt = parseKickoffUtcMs(first.kickoff);
  return kt != null && kt <= Date.now();
}

/**
 * Tillfälligt admin-fönster som häver slutspels-faslåset.
 * Medan fönstret är öppet låses varje slutspelsmatch individuellt vid sin egen kickoff,
 * så redan startade/spelade matcher förblir låsta.
 * @param {string|number|null|undefined} knockoutUnlockUntil ISO-tid (eller ms) då fönstret stänger
 */
export function isKnockoutTipUnlockActive(knockoutUnlockUntil) {
  if (!knockoutUnlockUntil) return false;
  const ms = parseKickoffUtcMs(knockoutUnlockUntil) ?? Date.parse(knockoutUnlockUntil);
  return Number.isFinite(ms) && Date.now() < ms;
}

/** En enskild slutspelsmatch som själv startat (LIVE/FINISHED eller passerad kickoff). */
function knockoutMatchStarted(m) {
  if (m.status === "LIVE" || m.status === "FINISHED") return true;
  const kt = parseKickoffUtcMs(m.kickoff);
  return kt != null && kt <= Date.now();
}

/**
 * @param {object} m match
 * @param {Array<object>|undefined|null} fixtures hela projektets fixtures (behövs för faslås)
 * @param {string|number|null} [knockoutUnlockUntil] tillfälligt fönster som häver slutspels-faslåset
 */
export function isMatchTipInputLocked(m, fixtures, knockoutUnlockUntil = null) {
  void knockoutUnlockUntil; // legacy admin-fönster; per-match-lås är nu standard
  if (!m) return true;
  const list = fixtures || [];
  const stage = m.stage || "";
  if (stage === "GROUP" || stage === "LP") {
    return isGroupPhaseLocked(list);
  }
  const h = String(m.home_code ?? "").trim();
  const a = String(m.away_code ?? "").trim();
  if (!h || !a) return true;
  return knockoutMatchStarted(m);
}
